import { OwnerType } from "@/domain/_shared/lib/owner-types";
import { FileCreateInput } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createFileMutationDocument } from "./file.gql";
import { EVENT_FILE_CREATED, trackFileEvent } from "./tracking";

export interface UseCreateFileProps {
  ownerId: string;
  ownerType: OwnerType;
  onCompleted?: () => void;
  onError?: (e: Error) => void;
}

export const useCreateFile = ({ ownerId, ownerType, onCompleted, onError }: UseCreateFileProps) => {
  const queryClient = useQueryClient();

  const { mutateAsync: createFile } = useMutation({
    mutationFn: async (input: FileCreateInput) => {
      const client = createOperationClient("createFile");
      return await client.request(createFileMutationDocument, { input });
    },
    onSuccess: (data) => {
      const file = data?.createFile;
      if (file) {
        trackFileEvent(EVENT_FILE_CREATED, {
          id: file.id,
          name: file.name,
          ownerId: file.ownerId,
        });
      }

      // Invalidate file queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.list(ownerId, ownerType),
      });

      if (onCompleted) {
        onCompleted();
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    createFile,
  };
};
